<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useDialog } from 'primevue/usedialog'
import CDashboardClassroomListUserDialog from '@/presentation/components/dashboard/CDashboardClassroomListUserDialog.vue'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import { default as PSkeleton } from 'primevue/skeleton'
import { default as PMessage } from 'primevue/message'
import CBaseCard from '@/presentation/components/base/CBaseCard.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useAuthStore } from '@/presentation/stores/auth.ts'

// COMPOSABLES
const dialog = useDialog()
const dashboardClassroomStore = useDashboardClassroomStore()
const authStore = useAuthStore()

// STORE REFS
const {
  dashboardSelectedClassroom: classroom,
  dashboardClassroomStoreIsLoading: isLoading,
  dashboardClassroomStoreError: error
} = storeToRefs(dashboardClassroomStore)

const {
  isAdmin, isPerdir, isCpe, isTeacher
} = storeToRefs(authStore)

// LOCAL STATE
const localLoading = ref(false)
const localError = ref('')

// COMPUTED
const hasError = computed(() => (error.value && error.value.hasError) || localError.value !== '')

const errorMessage = computed(
  () => localError.value || (error.value?.hasError ? error.value.message : '')
)

const isDataLoading = computed(() => isLoading.value || localLoading.value)

const hasData = computed(() => !isDataLoading.value && !!classroom.value)

const cards = computed(() => [
  {
    key: 'students',
    title: 'élève(s)',
    value: classroom.value?.students?.length ?? 0,
    icon: 'user',
    visible: true,
    action: () => openUserListDialog('students')
  },
  {
    key: 'teachers',
    title: 'enseignant(s) référent(s)',
    value: classroom.value?.teachers?.length ?? 0,
    icon: 'user',
    visible: true,
    action: () => openUserListDialog('teachers')
  },
  {
    key: 'code',
    title: 'Code classe',
    value: classroom.value?.code,
    icon: 'lock',
    visible: isAdmin.value || isPerdir.value || isCpe.value || isTeacher.value,
    tooltip: 'Ce code est à communiquer aux élèves pour leur inscription.',
  }
])


// METHODS
/**
 * Ouvre un dialogue affichant une liste d'utilisateurs
 * @param userType Type d'utilisateurs à afficher ('students' ou 'teachers')
 */
function openUserListDialog(userType: 'students' | 'teachers') {
  try {
    let users = []
    let header = ''

    if (userType === 'students') {
      if (!classroom.value?.students) {
        throw new Error("La liste des élèves n'est pas disponible")
      }
      users = classroom.value.students
      header = 'Liste des élèves'
    } else {
      // teachers
      if (!classroom.value?.teachers) {
        throw new Error("La liste des enseignants n'est pas disponible")
      }
      users = classroom.value.teachers
      header = 'Liste des enseignants référents'
    }

    dialog.open(CDashboardClassroomListUserDialog, {
      props: {
        header,
        modal: true,
        dismissableMask: true,
        draggable: false,
        style: 'width: 50vw',
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw'
        }
      },
      data: { users, isStudentList: userType === 'students' }
    })
  } catch (err) {
    localError.value = err instanceof Error ? err.message : "Erreur lors de l'ouverture de la liste"
    console.error(`Erreur lors de l'ouverture de la liste des ${userType}:`, err)
  }
}

// LIFECYCLE
onMounted(() => {
  // Réinitialiser les erreurs locales au montage
  localError.value = ''
})
</script>

<template>
  <!-- Message d'erreur -->
  <p-message v-if="hasError" severity="error" class="mb-4 w-full">
    {{ errorMessage }}
  </p-message>

  <!-- État de chargement -->
  <div
    v-if="isDataLoading"
    class="col-span-12 grid max-h-min grid-cols-12 flex-wrap justify-start gap-4 sm:gap-2 md:gap-6"
  >
    <div v-for="i in 2" :key="i" class="col flex-initial p-4">
      <p-skeleton width="100%" height="60px" />
    </div>
  </div>

  <!-- État vide -->
  <div
    v-else-if="!hasData"
    class="border-bo-brand-secondary rounded-border col-span-12 flex items-center justify-center border bg-white p-4"
  >
    <i class="pi pi-exclamation-circle mr-2 text-yellow-500"></i>
    <span>Aucune donnée disponible</span>
  </div>

  <!-- Contenu principal -->
  <div
    v-else
    class="col-span-12 flex max-h-min grid-cols-8 flex-wrap justify-start gap-4 sm:gap-2 md:gap-6"
  >
    <c-base-card
      v-for="card in cards.filter(c => c.visible)"
      :key="`card_${card.key}`"
      @click="card.action"
      padding="small"
      :gap="2"
      class="cursor-pointer"
      v-tooltip.bottom="card?.tooltip ? { value: `${card?.tooltip}`, pt: { text: 'text-sm' }} : null"
    >
      <template #content>
        <div>
          <font-awesome-icon :icon="card.icon" class="mr-1" />
          <span class="ml-2 text-xl font-bold">{{ card.value }}</span>
        </div>
        <div>
          <span class="text-base">{{ card.title }}</span>
          <font-awesome-icon v-if="card.action != null" icon="arrow-right" class="ml-2 text-primary align-baseline" />
        </div>
      </template>
    </c-base-card>
  </div>
</template>
